/**
 * advancedUI.js — 4 уникальных UI-фичи, которых нет ни у одного DICOM viewer
 *
 * 1. Command Palette (Ctrl+K) — fuzzy search по всем инструментам/пресетам/действиям
 * 2. Radial Menu (ПКМ на viewport) — круговое меню вокруг курсора
 * 3. Ambient Glow — цветной ореол viewport по модальности
 * 4. Smart Toolbar — адаптивная панель инструментов по частоте использования
 *
 * Подключение в uiManager.js:
 *   import { initAdvancedUI } from './advancedUI.js';
 *   initAdvancedUI(sharedCtx);  // после initContextUI
 */

import { on as busOn } from '../core/eventBus.js';
import { log } from '../utils/logger.js';

// [FIX] AbortController для очистки глобальных слушателей
let _ac = new AbortController();

const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);

let _ctx = null;

// ══════════════════════════════════════════════════════════════
// 1. COMMAND PALETTE (Ctrl+K)
// ══════════════════════════════════════════════════════════════

const PALETTE_COMMANDS = [
  // W/L Presets
  { id: 'wl-abdomen', label: 'Живот', alias: 'abdomen', cat: 'W/L', action: () => _clickPreset(400, 40) },
  { id: 'wl-lung', label: 'Лёгкие', alias: 'lung', cat: 'W/L', action: () => _clickPreset(1500, -600) },
  { id: 'wl-bone', label: 'Кости', alias: 'bone', cat: 'W/L', action: () => _clickPreset(2500, 480) },
  { id: 'wl-brain', label: 'Мозг', alias: 'brain', cat: 'W/L', action: () => _clickPreset(80, 40) },
  { id: 'wl-stroke', label: 'Инсульт', alias: 'stroke', cat: 'W/L', action: () => _clickPreset(40, 40) },
  { id: 'wl-liver', label: 'Печень', alias: 'liver', cat: 'W/L', action: () => _clickPreset(150, 30) },
  { id: 'wl-angio', label: 'Ангио', alias: 'angio', cat: 'W/L', action: () => _clickPreset(600, 170) },
  { id: 'wl-pe', label: 'PE (ТЭЛА)', alias: 'pe pulmonary', cat: 'W/L', action: () => _clickPreset(700, 100) },
  { id: 'wl-mediastinum', label: 'Средостение', alias: 'mediastinum', cat: 'W/L', action: () => _clickPreset(350, 50) },
  { id: 'wl-soft', label: 'Мягкие ткани', alias: 'soft tissue', cat: 'W/L', action: () => _clickPreset(350, 50) },
  // Tools
  { id: 'tool-wl', label: 'Окно/Уровень', alias: 'window level', cat: 'Инструмент', action: () => _activateTool('WindowLevel') },
  { id: 'tool-scroll', label: 'Прокрутка срезов', alias: 'scroll stack', cat: 'Инструмент', action: () => _activateTool('StackScroll') },
  { id: 'tool-zoom', label: 'Масштаб', alias: 'zoom', cat: 'Инструмент', action: () => _activateTool('Zoom') },
  { id: 'tool-pan', label: 'Перемещение', alias: 'pan hand', cat: 'Инструмент', action: () => _activateTool('Pan') },
  { id: 'tool-length', label: 'Линейка', alias: 'ruler length measure', cat: 'Инструмент', action: () => _activateTool('Length') },
  { id: 'tool-angle', label: 'Угол', alias: 'angle', cat: 'Инструмент', action: () => _activateTool('Angle') },
  { id: 'tool-cobb', label: 'Угол Кобба', alias: 'cobb scoliosis', cat: 'Инструмент', action: () => _activateTool('CobbAngle') },
  { id: 'tool-arrow', label: 'Стрелка', alias: 'arrow annotate', cat: 'Инструмент', action: () => _activateTool('ArrowAnnotate') },
  { id: 'tool-probe', label: 'Зонд HU', alias: 'probe hu density', cat: 'Инструмент', action: () => _activateTool('Probe') },
  { id: 'tool-ellipse', label: 'Эллиптический ROI', alias: 'ellipse roi', cat: 'Инструмент', action: () => _activateTool('EllipticalROI') },
  { id: 'tool-rect', label: 'Прямоугольный ROI', alias: 'rectangle roi', cat: 'Инструмент', action: () => _activateTool('RectangleROI') },
  { id: 'tool-circle', label: 'Круглый ROI', alias: 'circle roi', cat: 'Инструмент', action: () => _activateTool('CircleROI') },
  { id: 'tool-freehand', label: 'Свободное рисование', alias: 'freehand pencil', cat: 'Инструмент', action: () => _activateTool('PlanarFreehandROI') },
  { id: 'tool-crosshair', label: 'Перекрестие', alias: 'crosshair', cat: 'Инструмент', action: () => _activateTool('Crosshairs') },
  { id: 'tool-magnify', label: 'Лупа', alias: 'magnify loupe', cat: 'Инструмент', action: () => _activateTool('Magnify') },
  { id: 'tool-rotate', label: '3D Вращение', alias: 'rotate 3d trackball', cat: 'Инструмент', action: () => _activateTool('TrackballRotate') },
  { id: 'tool-brush', label: 'Кисть', alias: 'brush segment', cat: 'Инструмент', action: () => _activateTool('Brush') },
  // Layouts
  { id: 'layout-1x1', label: '1×1', alias: '1x1 single', cat: 'Раскладка', action: () => $('btn-layout-1x1')?.click() },
  { id: 'layout-1x2', label: '1×2', alias: '1x2 dual', cat: 'Раскладка', action: () => $('btn-layout-1x2')?.click() },
  { id: 'layout-2x2', label: '2×2', alias: '2x2 quad', cat: 'Раскладка', action: () => $('btn-layout-2x2')?.click() },
  { id: 'layout-mpr', label: 'MPR', alias: 'mpr multiplanar', cat: 'Раскладка', action: () => $('btn-layout-mpr')?.click() },
  { id: 'mode-2d', label: 'Режим 2D', alias: '2d stack', cat: 'Раскладка', action: () => $('btn-mode-2d')?.click() },
  { id: 'mode-3d', label: 'Режим 3D', alias: '3d volume', cat: 'Раскладка', action: () => $('btn-mode-3d')?.click() },
  // Actions
  { id: 'act-reset', label: 'Сброс', alias: 'reset clear', cat: 'Действие', action: () => $('btn-reset')?.click() },
  { id: 'act-invert', label: 'Инверсия', alias: 'invert negative', cat: 'Действие', action: () => $('btn-invert')?.click() },
  { id: 'act-screenshot', label: 'Снимок экрана', alias: 'screenshot capture', cat: 'Действие', action: () => $('btn-screenshot')?.click() },
  { id: 'act-fullscreen', label: 'Полный экран', alias: 'fullscreen', cat: 'Действие', action: () => $('btn-fullscreen')?.click() },
  { id: 'act-flip-h', label: 'Отразить горизонтально', alias: 'flip horizontal', cat: 'Действие', action: () => $('btn-flip-h')?.click() },
  { id: 'act-flip-v', label: 'Отразить вертикально', alias: 'flip vertical', cat: 'Действие', action: () => $('btn-flip-v')?.click() },
  { id: 'act-rotate-cw', label: 'Поворот +90°', alias: 'rotate clockwise', cat: 'Действие', action: () => $('btn-rotate-cw')?.click() },
  // [11.0.9] Цель сменилась: btn-print удалена как дубль, печать ведёт
  // btn-report-print на вкладке «Отчёты». Команда палитры осталась живой.
  { id: 'act-print', label: 'Печать', alias: 'print отчёт', cat: 'Действие', action: () => $('btn-report-print')?.click() },
  // Clinical
  { id: 'clin-agatston', label: 'Индекс Агатстона', alias: 'agatston calcium score', cat: 'Клиника', action: () => $('btn-agatston')?.click() },
  { id: 'clin-ctr', label: 'CTR (кардиоторакальный)', alias: 'ctr cardiothoracic', cat: 'Клиника', action: () => $('btn-ctr')?.click() },
  { id: 'clin-tavi', label: 'TAVI Планирование', alias: 'tavi valve', cat: 'Клиника', action: () => $('btn-tavi')?.click() },
  { id: 'clin-emphysema', label: 'Эмфизема', alias: 'emphysema', cat: 'Клиника', action: () => $('btn-emphysema')?.click() },
  // 3D Render
  { id: 'r3d-bone', label: 'CT Кости 3D', alias: 'bone 3d render', cat: '3D', action: () => _clickRenderPreset('ct-bone-3d') },
  { id: 'r3d-vascular', label: 'CT Сосуды 3D', alias: 'vascular 3d', cat: '3D', action: () => _clickRenderPreset('ct-vascular') },
  { id: 'r3d-cardiac', label: 'Сердце 3D', alias: 'cardiac heart 3d', cat: '3D', action: () => _clickRenderPreset('cardiac-3d') },
  { id: 'r3d-lung', label: 'Лёгкие 3D', alias: 'lung 3d', cat: '3D', action: () => _clickRenderPreset('ct-lung-3d') },
  { id: 'r3d-angio', label: 'Ангио 3D', alias: 'angio 3d', cat: '3D', action: () => _clickRenderPreset('angio-3d') },
  { id: 'r3d-brain', label: 'MRI Мозг 3D', alias: 'brain mri 3d', cat: '3D', action: () => _clickRenderPreset('mri-brain-3d') },
  // Files
  { id: 'file-open', label: 'Открыть файлы', alias: 'open files', cat: 'Файл', action: () => $('btn-open-file')?.click() },
  { id: 'file-folder', label: 'Открыть папку', alias: 'open folder directory', cat: 'Файл', action: () => $('btn-open-folder')?.click() },
  { id: 'file-archive', label: 'Архив пациентов', alias: 'archive patient database', cat: 'Файл', action: () => $('btn-archive')?.click() },
  { id: 'file-wado', label: 'WADO URL', alias: 'wado dicomweb pacs', cat: 'Файл', action: () => $('btn-open-url')?.click() },
  { id: 'file-export-zip', label: 'Экспорт ZIP', alias: 'export zip', cat: 'Файл', action: () => $('btn-export-zip')?.click() },
  { id: 'file-clear', label: 'Очистить всё', alias: 'clear all', cat: 'Файл', action: () => $('btn-clear')?.click() },
  // Panels
  { id: 'panel-left', label: 'Панель серий', alias: 'series panel left', cat: 'Панель', action: () => $('btn-sidebar-left-toggle')?.click() },
  { id: 'panel-right', label: 'Панель настроек', alias: 'settings panel right presets разделы sidebar', cat: 'Панель', action: () => $('btn-sidebar-right-toggle')?.click() },
  // [11.0.1] Команда палитры «Enterprise» снята: кнопки-цели больше нет.
  // [11.0.2] Команда «Правый сайдбар» (panel-sidebar) снята следом — и по той
  // же причине, что и пункт «Разделы» в нижнем ряду. Она дёргала класс
  // `sidebar-open`, для которого нет ни одного правила стилей, то есть не
  // работала нигде — ни на телефоне, ни на десктопе.
  //
  // Это стоит отметить отдельно: именно её в 11.0.1 поставили ВЗАМЕН удалённой
  // кнопки Enterprise и назвали честной заменой. Замена оказалась такой же
  // вывеской, как и то, что заменяли.
  //
  // Дубля не потеряно: строкой выше стоит `panel-right` — «Панель настроек»,
  // которая открывает тот же правый сайдбар настоящим путём, нажатием
  // btn-sidebar-right-toggle. В её alias добавлено слово «разделы», чтобы
  // поиск по палитре находил панель по прежнему запросу.
];

let _paletteEl = null;
let _paletteInput = null;
let _paletteList = null;
let _paletteIdx = 0;
let _filtered = [];

function _buildPalette() {
  if (_paletteEl) return;
  const div = document.createElement('div');
  div.id = 'cmd-palette';
  div.className = 'cmd-palette';
  div.innerHTML = `
    <div class="cmd-palette-inner">
      <input type="text" class="cmd-input" placeholder="Введите команду... (инструмент, пресет, действие)" spellcheck="false" autocomplete="off">
      <div class="cmd-list"></div>
      <div class="cmd-footer"><span>↑↓ навигация</span><span>Enter выбор</span><span>Esc закрыть</span></div>
    </div>`;
  document.body.appendChild(div);
  _paletteEl = div;
  _paletteInput = div.querySelector('.cmd-input');
  _paletteList = div.querySelector('.cmd-list');

  _paletteInput.addEventListener('input', () => _filterPalette(_paletteInput.value), { signal: _ac.signal });
  _paletteInput.addEventListener('keydown', _handlePaletteKeys, { signal: _ac.signal });
  div.addEventListener('click', (e) => {
    if (e.target === div) _closePalette();
    const item = e.target.closest('.cmd-item');
    if (item) { _executePaletteItem(parseInt(item.dataset.idx)); }
  }, { signal: _ac.signal });
}

function _openPalette() {
  _buildPalette();
  _paletteEl.classList.add('open');
  _paletteInput.value = '';
  _filterPalette('');
  setTimeout(() => _paletteInput.focus(), 50);
}

function _closePalette() {
  if (_paletteEl) _paletteEl.classList.remove('open');
}

function _filterPalette(query) {
  const q = query.toLowerCase().trim();
  _filtered = q ? PALETTE_COMMANDS.filter(c =>
    c.label.toLowerCase().includes(q) ||
    c.alias.toLowerCase().includes(q) ||
    c.cat.toLowerCase().includes(q)
  ) : PALETTE_COMMANDS.slice(0, 12);
  _paletteIdx = 0;
  _renderPaletteList();
}

function _renderPaletteList() {
  const CAT_ICONS = { 'W/L': '◐', 'Инструмент': '🔧', 'Раскладка': '⊞', 'Действие': '⚡', 'Клиника': '❤', '3D': '🧊', 'Файл': '📁', 'Панель': '▫' };
  _paletteList.innerHTML = _filtered.slice(0, 10).map((c, i) =>
    `<div class="cmd-item ${i === _paletteIdx ? 'active' : ''}" data-idx="${i}">
      <span class="cmd-cat-icon">${CAT_ICONS[c.cat] || '•'}</span>
      <span class="cmd-label">${c.label}</span>
      <span class="cmd-cat">${c.cat}</span>
    </div>`
  ).join('');
}

function _handlePaletteKeys(e) {
  if (e.key === 'Escape') { _closePalette(); return; }
  if (e.key === 'ArrowDown') { e.preventDefault(); _paletteIdx = Math.min(_paletteIdx + 1, _filtered.length - 1); _renderPaletteList(); return; }
  if (e.key === 'ArrowUp') { e.preventDefault(); _paletteIdx = Math.max(_paletteIdx - 1, 0); _renderPaletteList(); return; }
  if (e.key === 'Enter') { e.preventDefault(); _executePaletteItem(_paletteIdx); return; }
}

function _executePaletteItem(idx) {
  const cmd = _filtered[idx];
  if (!cmd) return;
  _closePalette();
  try { cmd.action(); } catch (e) { log.warn('[Palette]', e); }
  _trackToolUsage(cmd.id);
}

// ══════════════════════════════════════════════════════════════
// 2. RADIAL MENU (ПКМ на viewport)
// ══════════════════════════════════════════════════════════════

const RADIAL_ITEMS = [
  { label: 'W/L', icon: '◐', tool: 'WindowLevel' },
  { label: 'Срезы', icon: '☰', tool: 'StackScroll' },
  { label: 'Зум', icon: '⊕', tool: 'Zoom' },
  { label: 'Рука', icon: '✋', tool: 'Pan' },
  { label: 'Линейка', icon: '📏', tool: 'Length' },
  { label: 'Угол', icon: '∠', tool: 'Angle' },
  { label: 'Стрелка', icon: '➜', tool: 'ArrowAnnotate' },
  { label: 'ROI', icon: '⬭', tool: 'EllipticalROI' },
  // [6.7.24] Мишень для радиохирургии — прямо в радиальном меню,
  // а не в отдельной панели сбоку: тут инструменты и ищут.
  { label: 'Мастер', icon: '▶', action: 'rt-wizard' },
  { label: 'Обвести', icon: '✏️', action: 'rt-draw' },
  { label: 'Опухоль', icon: '🎯', action: 'rt-seg' },
  { label: 'RT-панель', icon: '📋', action: 'rt-panel' },
  { label: 'Итог', icon: '📊', action: 'rt-summary' },
  { label: 'Справка', icon: '📖', action: 'rt-help' },
];

/** [6.7.24] Действия радиального меню, не связанные с инструментами Cornerstone. */
async function _radialAction(action) {
  try {
    if (action === 'rt-wizard') {
      const m = await import('../io/rtWizardModule.js');
      m.startWizard();
    } else if (action === 'rt-draw') {
      const m = await import('../io/rtDrawModule.js');
      const on = m.armDraw(true);
      window.showToast?.(on ? '✏️ Обведите опухоль, зажав левую кнопку' : '❌ Не удалось включить', on ? 'info' : 'error', 4000);
    } else if (action === 'rt-seg') {
      const m = await import('../io/rtSegmentModule.js');
      m.armSegmentByClick(true);
      window.showToast?.('🎯 Кликните в центр опухоли', 'info', 4000);
    } else if (action === 'rt-summary') {
      const m = await import('../io/rtReportModule.js');
      m.showSummary();
    } else if (action === 'rt-help') {
      const m = await import('../io/rtKnowledgeModule.js');
      m.showContents();
    } else if (action === 'rt-panel') {
      const panel = document.getElementById('rtstruct-panel');
      if (panel) panel.style.display = panel.style.display === 'none' ? 'flex' : 'none';
      else document.getElementById('rtstruct-launcher')?.click();
    }
  } catch (e) {
    console.error('[Radial] RT-действие не удалось:', e);
    window.showToast?.('❌ ' + e.message, 'error', 4000);
  }
}

let _radialEl = null;
let _radialActive = -1;
let _radialDrag = { active: false, offsetX: 0, offsetY: 0 };

function _buildRadial() {
  if (_radialEl) return;
  const div = document.createElement('div');
  div.id = 'radial-menu';
  div.className = 'radial-menu';

  const r = 72;
  const items = RADIAL_ITEMS.map((item, i) => {
    const angle = (i / RADIAL_ITEMS.length) * 360 - 90;
    const rad = angle * (Math.PI / 180);
    const x = Math.cos(rad) * r;
    const y = Math.sin(rad) * r;
    return `<div class="radial-item" data-idx="${i}" data-tool="${item.tool || ''}" data-action="${item.action || ''}" style="transform:translate(${x}px,${y}px)" title="${item.label}">
      <span class="radial-icon">${item.icon}</span>
      <span class="radial-label">${item.label}</span>
    </div>`;
  }).join('');

  div.innerHTML = `<div class="radial-center" title="Перетащите">⚙</div>${items}`;
  document.body.appendChild(div);
  _radialEl = div;

  // Click handler
  div.addEventListener('click', (e) => {
    const item = e.target.closest('.radial-item');
    if (item) {
      if (item.dataset.action) {
        _radialAction(item.dataset.action);
        _trackToolUsage('radial-' + item.dataset.action);
      } else {
        _activateTool(item.dataset.tool);
        _trackToolUsage('radial-' + item.dataset.tool);
      }
      _closeRadial();
      return;
    }
    if (!e.target.closest('.radial-center')) _closeRadial();
  }, { signal: _ac.signal });

  // Drag by center gear
  const center = div.querySelector('.radial-center');

  center.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = div.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    _radialDrag = { active: true, offsetX: e.clientX - cx, offsetY: e.clientY - cy };
    center.setPointerCapture(e.pointerId);
    div.classList.add('dragging');
  }, { signal: _ac.signal });

  center.addEventListener('pointermove', (e) => {
    if (!_radialDrag.active) return;
    e.preventDefault();
    const nx = e.clientX - _radialDrag.offsetX;
    const ny = e.clientY - _radialDrag.offsetY;
    const vpc = $('viewport-container');
    if (vpc) {
      const vr = vpc.getBoundingClientRect();
      div.style.left = Math.max(vr.left + 50, Math.min(nx, vr.right - 50)) + 'px';
      div.style.top = Math.max(vr.top + 50, Math.min(ny, vr.bottom - 50)) + 'px';
    } else {
      div.style.left = nx + 'px';
      div.style.top = ny + 'px';
    }
  }, { signal: _ac.signal });

  center.addEventListener('pointerup', () => {
    _radialDrag.active = false;
    div.classList.remove('dragging');
  }, { signal: _ac.signal });

  // Inject drag styles
  _injectRadialStyles();
}

function _injectRadialStyles() {
  if (document.getElementById('radial-drag-css')) return;
  const style = document.createElement('style');
  style.id = 'radial-drag-css';
  style.textContent = `
    .radial-menu .radial-center {
      width: 24px; height: 24px; left: -12px; top: -12px;
      display: flex; align-items: center; justify-content: center;
      cursor: grab; user-select: none; touch-action: none;
      font-size: 16px; transition: transform 0.15s;
      background: rgba(15,17,25,0.95); border: 1px solid var(--accent);
      border-radius: 50%; box-shadow: 0 0 12px var(--accent-glow);
    }
    .radial-menu .radial-center:hover { transform: scale(1.2); }
    .radial-menu .radial-center:active { cursor: grabbing; transform: scale(1.1); }
    .radial-menu.dragging { transition: none !important; }
    .radial-menu.dragging .radial-center { cursor: grabbing; }
  `;
  document.head.appendChild(style);
}

function _openRadial(x, y) {
  _buildRadial();
  _radialEl.style.left = x + 'px';
  _radialEl.style.top = y + 'px';
  _radialEl.classList.add('open');
  _radialActive = -1;
}

function _closeRadial() {
  if (_radialEl) _radialEl.classList.remove('open');
}

// ══════════════════════════════════════════════════════════════
// 3. AMBIENT GLOW — цветной ореол viewport по модальности
// ══════════════════════════════════════════════════════════════

const AMBIENT_COLORS = {
  'ct-chest': 'rgba(59,130,246,0.12)',     // синий
  'ct-head': 'rgba(168,85,247,0.12)',      // фиолетовый
  'ct-cardiac': 'rgba(239,68,68,0.10)',    // красный
  'ct-abdomen': 'rgba(34,197,94,0.10)',    // зелёный
  'ct-spine': 'rgba(234,179,8,0.10)',      // жёлтый
  'mri-brain': 'rgba(139,92,246,0.12)',    // пурпурный
  'mri-cardiac': 'rgba(236,72,153,0.10)', // розовый
  'xray': 'rgba(148,163,184,0.08)',        // серый
  'mammo': 'rgba(248,113,113,0.08)',       // пастель-красный
  'pet': 'rgba(251,191,36,0.12)',          // оранжевый
  'us': 'rgba(34,211,238,0.10)',           // голубой
};

function _setAmbientGlow(contextKey) {
  const vpc = $('viewport-container');
  if (!vpc) return;
  const color = AMBIENT_COLORS[contextKey];
  if (color) {
    vpc.style.boxShadow = `inset 0 0 60px ${color}, inset 0 0 120px ${color.replace(/[\d.]+\)$/, '0.04)')}`;
  } else {
    vpc.style.boxShadow = '';
  }
}

// ══════════════════════════════════════════════════════════════
// 4. SMART TOOLBAR — адаптивная панель по частоте использования
// ══════════════════════════════════════════════════════════════

const USAGE_KEY = 'mikhmel_tool_usage';
const SMART_TOOLBAR_COUNT = 8;

function _getToolUsage() {
  try { return JSON.parse(localStorage.getItem(USAGE_KEY) || '{}'); } catch { return {}; }
}

function _trackToolUsage(toolId) {
  const usage = _getToolUsage();
  usage[toolId] = (usage[toolId] || 0) + 1;
  try { localStorage.setItem(USAGE_KEY, JSON.stringify(usage)); } catch { /* non-critical */ }
}

function _reorderToolbar() {
  const toolbar = $('toolbar');
  if (!toolbar || window.innerWidth <= 768) return;

  const usage = _getToolUsage();
  const toolBtns = [...toolbar.querySelectorAll('.tool-btn[data-tool]')];
  if (!toolBtns.length) return;

  // Сортируем по usage (desc), потом по original order
  const sorted = toolBtns
    .map((btn, origIdx) => ({ btn, tool: btn.dataset.tool, count: usage[btn.dataset.tool] || usage['radial-' + btn.dataset.tool] || 0, origIdx }))
    .sort((a, b) => b.count - a.count || a.origIdx - b.origIdx);

  // Top N получают подсветку, остальные получают сниженную яркость
  sorted.forEach((item, i) => {
    item.btn.classList.toggle('smart-promoted', i < SMART_TOOLBAR_COUNT && item.count > 0);
    item.btn.classList.toggle('smart-dimmed', i >= SMART_TOOLBAR_COUNT && item.count > 0);
  });
}

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════

function _clickPreset(ww, wc) {
  const chip = [...$$('.preset-chip[data-ww]')].find(c =>
    parseInt(c.dataset.ww) === ww && parseInt(c.dataset.wc) === wc
  );
  if (chip) chip.click();
}

function _clickRenderPreset(name) {
  const chip = document.querySelector(`.preset-chip[data-render-preset="${name}"], .sl-vrt-btn[data-render-preset="${name}"]`);
  if (chip) chip.click();
}

function _activateTool(toolName) {
  const btn = document.querySelector(`.tool-btn[data-tool="${toolName}"]`);
  if (btn) btn.click();
  _trackToolUsage(toolName);
}

// ══════════════════════════════════════════════════════════════
// INIT
// ══════════════════════════════════════════════════════════════

export function initAdvancedUI(ctx) {
  // [FIX] Cleanup previous listeners
  if (_ac) _ac.abort();
  _ac = new AbortController();
  _ctx = ctx;

  // ── Command Palette: Ctrl+K ──
  document.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      _openPalette();
    }
  }, { signal: _ac.signal });

  // ── Radial Menu: Right-click on viewport ──
  const vpc = $('viewport-container');
  if (vpc) {
    vpc.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation(); // [FIX] Блокируем contextMenu.js — показываем только радиальное меню
      _closeRadial();
      const ctxMenu = $('vp-ctx-menu');
      if (ctxMenu) ctxMenu.classList.remove('visible');
      _openRadial(e.clientX, e.clientY);
    }, { signal: _ac.signal });
    // [10.19.28] Долгое нажатие на телефоне открывает КРУГ — и только его.
    //
    // Раньше сенсорный путь был лишь в contextMenu.js со своим таймером, и на
    // тапе появлялись оба меню сразу (разбор — в шапке того файла). Теперь
    // касание обрабатывается здесь, рядом с самим кругом: своё нажатие надёжнее
    // синтетического contextmenu, который Android присылает не всегда.
    // Сдвиг пальца больше 10 пикселей отменяет — иначе круг вылезал бы при
    // обычной прокрутке срезов.
    let _тапТаймер = null, _тапX = 0, _тапY = 0;
    const _отменитьТап = () => { if (_тапТаймер) { clearTimeout(_тапТаймер); _тапТаймер = null; } };
    vpc.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { _отменитьТап(); return; }
      if (e.target.closest('button, .vp-zoom-btn, .vp-zoom-controls, .radial-menu')) return;
      const т = e.touches[0];
      _тапX = т.clientX; _тапY = т.clientY;
      _отменитьТап();
      _тапТаймер = setTimeout(() => {
        _тапТаймер = null;
        try { navigator.vibrate?.(10); } catch (err) { /* вибрации может не быть */ }
        _closeRadial();
        const ctxMenu = $('vp-ctx-menu');
        if (ctxMenu) ctxMenu.classList.remove('visible');
        _openRadial(_тапX, _тапY);
      }, 500);
    }, { passive: true, signal: _ac.signal });
    vpc.addEventListener('touchmove', (e) => {
      const т = e.touches[0];
      if (!т) { _отменитьТап(); return; }
      if (Math.abs(т.clientX - _тапX) > 10 || Math.abs(т.clientY - _тапY) > 10) _отменитьТап();
    }, { passive: true, signal: _ac.signal });
    vpc.addEventListener('touchend', _отменитьТап, { passive: true, signal: _ac.signal });
    vpc.addEventListener('touchcancel', _отменитьТап, { passive: true, signal: _ac.signal });

    // Close on any click outside
    document.addEventListener('click', (e) => {
      if (_radialEl && !_radialEl.contains(e.target)) _closeRadial();
    }, { signal: _ac.signal });
    document.addEventListener('touchstart', (e) => {
      if (_radialEl && !_radialEl.contains(e.target)) _closeRadial();
    }, { passive: true, signal: _ac.signal });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') _closeRadial();
    }, { signal: _ac.signal });
  }

  // ── Ambient Glow: listen for context changes ──
  busOn('context:changed', (data) => {
    if (data?.context) _setAmbientGlow(data.context);
  });

  // ── Smart Toolbar: reorder on load + periodically ──
  setTimeout(_reorderToolbar, 2000);
  // Reorder every time user switches tool
  document.addEventListener('click', (e) => {
    if (e.target.closest('.tool-btn[data-tool]')) {
      const tool = e.target.closest('.tool-btn[data-tool]').dataset.tool;
      _trackToolUsage(tool);
      setTimeout(_reorderToolbar, 100);
    }
  }, { signal: _ac.signal });

  // ── Viewport Morphing: intercept layout buttons ──
  _initViewportMorphing();

  // ── Quick W/L: double-click on viewport ──
  _initQuickWL();

  // Smart W/L hover indicator (Ctrl+mousemove)
  _initSmartWLHover();

  log.debug('[AdvancedUI] Initialized: Command Palette (Ctrl+K), Radial Menu (RMB), Ambient Glow, Smart Toolbar, Viewport Morphing, Quick W/L');
}

// ══════════════════════════════════════════════════════════════
// 5. VIEWPORT MORPHING — плавные CSS-анимированные переходы layout
// ══════════════════════════════════════════════════════════════

function _initViewportMorphing() {
  const vpc = $('viewport-container');
  if (!vpc) return;

  // Intercept layout button clicks
  const layoutBtns = $$('[data-layout]');
  layoutBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      _morphViewport(vpc, btn.dataset.layout);
    }, { capture: true, signal: _ac.signal }); // capture — animate before actual layout change
  });

  // Intercept mode buttons too
  const modeBtns = $$('[data-mode]');
  modeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      _morphViewport(vpc, btn.dataset.mode);
    }, { capture: true, signal: _ac.signal });
  });
}

let _morphTimer = null;

function _morphViewport(vpc, targetLayout) {
  // Don't animate on mobile
  if (window.innerWidth <= 768) return;
  // Don't overlap animations
  if (_morphTimer) return;

  // Snapshot current state
  const children = [...vpc.children];
  children.forEach(child => {
    if (child.nodeType !== 1) return;
    // Save current bounds
    const rect = child.getBoundingClientRect();
    child._morphRect = rect;
    child.style.transition = 'none';
  });

  // After browser reflows with new layout, animate from old position
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      children.forEach(child => {
        if (!child._morphRect || child.nodeType !== 1) return;
        const oldRect = child._morphRect;
        const newRect = child.getBoundingClientRect();

        // Calculate delta
        const dx = oldRect.left - newRect.left;
        const dy = oldRect.top - newRect.top;
        const sw = oldRect.width / Math.max(newRect.width, 1);
        const sh = oldRect.height / Math.max(newRect.height, 1);

        if (Math.abs(dx) < 2 && Math.abs(dy) < 2 && Math.abs(sw - 1) < 0.01) {
          child.style.transition = '';
          delete child._morphRect;
          return;
        }

        // Apply inverse transform (start at old position)
        child.style.transform = `translate(${dx}px, ${dy}px) scale(${sw}, ${sh})`;
        child.style.transformOrigin = 'top left';
        child.style.transition = 'none';

        // Force reflow
        child.offsetHeight;

        // Animate to new position
        child.style.transition = 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
        child.style.transform = '';

        delete child._morphRect;
      });

      // Clean up after animation
      _morphTimer = setTimeout(() => {
        children.forEach(child => {
          if (child.nodeType !== 1) return;
          child.style.transition = '';
          child.style.transform = '';
          child.style.transformOrigin = '';
        });
        _morphTimer = null;
      }, 450);
    });
  });
}

// ══════════════════════════════════════════════════════════════
// 6. QUICK W/L — двойной клик по ткани → auto W/L по HU
// ══════════════════════════════════════════════════════════════

function _initQuickWL() {
  const vpc = $('viewport-container');
  if (!vpc) return;

  let _lastClickTime = 0;
  let _lastClickPos = { x: 0, y: 0 };

  vpc.addEventListener('pointerdown', (e) => {
    // Only left button double-click
    if (e.button !== 0) return;

    const now = Date.now();
    const dx = Math.abs(e.clientX - _lastClickPos.x);
    const dy = Math.abs(e.clientY - _lastClickPos.y);
    const dt = now - _lastClickTime;

    if (dt < 350 && dx < 10 && dy < 10) {
      // Double-click detected — get HU value at this point
      _quickWLAtPoint(e);
      _lastClickTime = 0; // reset to prevent triple
    } else {
      _lastClickTime = now;
      _lastClickPos = { x: e.clientX, y: e.clientY };
    }
  }, { signal: _ac.signal });
}

function _quickWLAtPoint(e) {
  try {
    // Find the viewport element under cursor
    const vpEl = e.target.closest('[data-viewport-uid]') || e.target.closest('#viewport-container > div');
    if (!vpEl) return;

    // Get Cornerstone viewport
    const cs = window.cornerstone;
    const re = cs?.getRenderingEngine?.('mikhmelRenderingEngine');
    if (!re) return;

    // Find viewport by element
    let viewport = null;
    for (const vp of re.getViewports()) {
      if (vp.element === vpEl || vpEl.contains(vp.element) || vp.element?.contains(vpEl)) {
        viewport = vp;
        break;
      }
    }
    if (!viewport) return;

    // Get world position from canvas coordinates
    const canvasPos = [
      e.clientX - vpEl.getBoundingClientRect().left,
      e.clientY - vpEl.getBoundingClientRect().top
    ];

    // Try to get pixel value (HU)
    let huValue = null;

    // Method 1: canvasToWorld → getPixelData (stack viewport)
    if (viewport.canvasToWorld && viewport.getPixelData) {
      try {
        const worldPos = viewport.canvasToWorld(canvasPos);
        // For stack viewports
        const pixelData = viewport.getPixelData();
        if (pixelData) {
          const dims = viewport.getImageData?.()?.dimensions;
          if (dims) {
            const ix = Math.round(canvasPos[0] * dims[0] / vpEl.clientWidth);
            const iy = Math.round(canvasPos[1] * dims[1] / vpEl.clientHeight);
            if (ix >= 0 && ix < dims[0] && iy >= 0 && iy < dims[1]) {
              huValue = pixelData[iy * dims[0] + ix];
            }
          }
        }
      } catch (_) { /* fallback below */ }
    }

    // Method 2: use Probe tool value if available
    if (huValue === null && window.__eventBus) {
      // Fallback: estimate from current voiRange center
      const props = viewport.getProperties?.();
      if (props?.voiRange) {
        huValue = (props.voiRange.lower + props.voiRange.upper) / 2;
      }
    }

    if (huValue === null) return;

    // Determine optimal W/L based on tissue HU
    const wl = _huToOptimalWL(huValue);

    // Apply with smooth transition
    viewport.setProperties({
      voiRange: { lower: wl.wc - wl.ww / 2, upper: wl.wc + wl.ww / 2 }
    });
    viewport.render();

    // Update overlay
    const wlEl = $('overlay-wl');
    if (wlEl) wlEl.textContent = `W: ${wl.ww} / L: ${wl.wc}`;

    // Visual feedback
    _showQuickWLFeedback(e.clientX, e.clientY, huValue, wl);

    log.debug(`[QuickWL] HU≈${huValue} → W:${wl.ww} L:${wl.wc} (${wl.tissue})`);
  } catch (err) {
    log.warn('[QuickWL]', err.message);
  }
}

/**
 * Map HU value to optimal Window/Level for that tissue type.
 */
function _huToOptimalWL(hu) {
  // Air
  if (hu < -800) return { ww: 1500, wc: -600, tissue: 'Воздух/Лёгкие' };
  // Lung parenchyma
  if (hu < -400) return { ww: 1500, wc: -500, tissue: 'Лёгочная ткань' };
  // Fat
  if (hu < -50) return { ww: 400, wc: -50, tissue: 'Жировая ткань' };
  // Water / fluid
  if (hu < 15) return { ww: 400, wc: 40, tissue: 'Жидкость' };
  // Soft tissue
  if (hu < 80) return { ww: 350, wc: 50, tissue: 'Мягкие ткани' };
  // Contrast-enhanced / blood
  if (hu < 200) return { ww: 500, wc: 100, tissue: 'Контраст/Кровь' };
  // Cancellous bone
  if (hu < 500) return { ww: 1500, wc: 400, tissue: 'Губч. кость' };
  // Cortical bone
  return { ww: 2500, wc: 480, tissue: 'Кортик. кость' };
}

/**
 * Show brief visual feedback at click position.
 */
function _showQuickWLFeedback(x, y, hu, wl) {
  let fb = $('quickwl-feedback');
  if (!fb) {
    fb = document.createElement('div');
    fb.id = 'quickwl-feedback';
    fb.className = 'quickwl-feedback';
    document.body.appendChild(fb);
  }
  fb.innerHTML = `<div class="qwl-hu">HU: ${Math.round(hu)}</div><div class="qwl-tissue">${wl.tissue}</div><div class="qwl-wl">W:${wl.ww} L:${wl.wc}</div>`;
  fb.style.left = x + 'px';
  fb.style.top = y + 'px';
  fb.classList.add('visible');

  clearTimeout(fb._timer);
  fb._timer = setTimeout(() => fb.classList.remove('visible'), 1500);
}

export function openCommandPalette() { _openPalette(); }
export function getToolUsageStats() { return _getToolUsage(); }
export function resetToolUsage() { try { localStorage.removeItem(USAGE_KEY); } catch { /* localStorage may be unavailable (private mode) */} _reorderToolbar(); }

// ══════════════════════════════════════════════════════════════
// 7. SMART W/L HOVER — показывает ткань под курсором при Ctrl
// ══════════════════════════════════════════════════════════════

let _smartWLTooltip = null;
let _smartWLActive = false;

function _initSmartWLHover() {
  const vpc = $('viewport-container');
  if (!vpc) return;

  vpc.addEventListener('mousemove', (e) => {
    if (!e.ctrlKey && !_smartWLActive) {
      if (_smartWLTooltip) _smartWLTooltip.style.display = 'none';
      return;
    }
    
    try {
      const vpEl = e.target.closest('[data-viewport-uid]') || e.target.closest('#viewport-container > div');
      if (!vpEl) return;

      const cs = window.cornerstone;
      const re = cs?.getRenderingEngine?.('mikhmelRenderingEngine');
      if (!re) return;

      let viewport = null;
      for (const vp of re.getViewports()) {
        if (vp.element === vpEl || vpEl.contains(vp.element) || vp.element?.contains(vpEl)) {
          viewport = vp; break;
        }
      }
      if (!viewport || !viewport.getPixelData) return;

      const canvasPos = [
        e.clientX - vpEl.getBoundingClientRect().left,
        e.clientY - vpEl.getBoundingClientRect().top
      ];

      let hu = null;
      try {
        const pixelData = viewport.getPixelData();
        const dims = viewport.getImageData?.()?.dimensions;
        if (pixelData && dims) {
          const ix = Math.round(canvasPos[0] * dims[0] / vpEl.clientWidth);
          const iy = Math.round(canvasPos[1] * dims[1] / vpEl.clientHeight);
          if (ix >= 0 && ix < dims[0] && iy >= 0 && iy < dims[1]) {
            hu = pixelData[iy * dims[0] + ix];
          }
        }
      } catch { /* non-critical */ }

      if (hu === null) return;

      const wl = _huToOptimalWL(hu);
      _showSmartWLTooltip(e.clientX, e.clientY, hu, wl);
    } catch { /* non-critical */ }
  }, { signal: _ac.signal });
}

function _showSmartWLTooltip(x, y, hu, wl) {
  if (!_smartWLTooltip) {
    _smartWLTooltip = document.createElement('div');
    _smartWLTooltip.id = 'smart-wl-tooltip';
    _smartWLTooltip.style.cssText = `
      position:fixed; z-index:30000; pointer-events:none;
      background:rgba(10,12,20,0.92); border:1px solid rgba(79,140,255,0.3);
      border-radius:8px; padding:6px 10px; font-size:11px; color:#ddd;
      backdrop-filter:blur(8px); white-space:nowrap;
      box-shadow:0 4px 12px rgba(0,0,0,0.4);
    `;
    document.body.appendChild(_smartWLTooltip);
  }
  _smartWLTooltip.style.display = 'block';
  _smartWLTooltip.style.left = (x + 16) + 'px';
  _smartWLTooltip.style.top = (y - 10) + 'px';
  _smartWLTooltip.innerHTML = `<b style="color:#4f8cff">${wl.tissue}</b> <span style="color:#888">HU:${Math.round(hu)}</span> <span style="color:#666">W:${wl.ww}/L:${wl.wc}</span><br><span style="font-size:9px;color:#555">Двойной клик — применить</span>`;
}

/** Toggle persistent Smart W/L mode (without Ctrl) */
export function toggleSmartWL() {
  _smartWLActive = !_smartWLActive;
  if (!_smartWLActive && _smartWLTooltip) _smartWLTooltip.style.display = 'none';
  return _smartWLActive;
}


/** [FIX] Очистка глобальных слушателей */
export function destroyAdvancedUI() {
  if (_ac) { _ac.abort(); _ac = null; }
}
